import React, { useState } from "react";
import { useLocation } from "react-router";

import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import ForgotPasswordModal from "./ForgotPasswordModal";
import logo from "assets/images/evoke_logo.png";

import { makeStyles } from "@mui/styles";
import { Form, Formik } from "formik";
import { loginValidationSchema } from "schemas/loginValidationSchema";
import { showErrorToast } from "utils/toast";
import { Button, Divider, IconButton } from "@mui/material";
import { cognitoLogin } from "services/auth.service";
import SubmitButton from "components/Button/SubmitButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"COPYRIGHT © "} {new Date().getFullYear()}
      {" EVOKE MEDICAL CARE - ALL RIGHTS RESERVED"}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.grey[900]
          : theme.palette.grey[50],
    },
  },
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),

    //autofill background color
    "& input:-webkit-autofill": {
      WebkitBoxShadow:
        theme.palette.type === "dark"
          ? "0 0 0 1000px #303030 inset"
          : "0 0 0 1000px #E0E0E0 inset",
      WebkitTextFillColor: theme.palette.type === "dark" ? "#fff" : "#000",
    },
  },
  submit: {
    margin: `${theme.spacing(2, 0, 2)} !important`,
  },
  secondaryLogin: {
    border: `2px solid ${theme.palette.secondary.main} !important`,
    marginTop: `${theme.spacing(1)} !important`,
    "&:hover": {
      backgroundColor: `${theme.palette.secondary.main} !important`,
      color: "#fff !important",
    },
  },
}));

export default function LoginForm(props) {
  const { loading, onSubmitLogin } = props;
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useLocation();
  const showWearablesButton =
    new URLSearchParams(search).get("wearables") === "true";

  const classes = useStyles();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordModal = () => {
    setPasswordModalOpen(!passwordModalOpen);
  };

  const onSubmit = (values) => {
    const { email, password, rememberMe } = values;
    onSubmitLogin(email, password, rememberMe);
  };

  const handleCognitoSignIn = async () => {
    try {
      setIsLoading(true);
      const response = await cognitoLogin();

      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        showErrorToast("Unable to retrieve login URL. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong, please try again.";
      showErrorToast(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <div className={classes.paper}>
        <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
          <img
            src={logo}
            alt="logo"
            style={{
              height: 60,
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        <Formik
          initialValues={{ email: "", password: "", rememberMe: false }}
          validationSchema={loginValidationSchema}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, touched, errors, values, handleChange }) => (
            <Form
              onSubmit={handleSubmit}
              className={classes.form}
              style={{ maxWidth: 480, margin: "0 auto" }}
            >
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                autoComplete="email"
                value={values.email}
                onChange={handleChange("email")}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                disabled={loading || isLoading}
              />

              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={values.password}
                onChange={handleChange("password")}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                disabled={loading || isLoading}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.rememberMe}
                      disabled={loading || isLoading}
                      onChange={handleChange("rememberMe")}
                    />
                  }
                  label="Remember me"
                />

                <Button
                  onClick={togglePasswordModal}
                  color="primary"
                  variant="text"
                  disabled={loading || isLoading}
                  sx={{ textTransform: "none", minWidth: "auto", padding: 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: loading || isLoading ? "not-allowed" : "pointer",
                      opacity: loading || isLoading ? 0.5 : 1,
                      pointerEvents: loading || isLoading ? "none" : "auto",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Button>
              </Box>

              <SubmitButton
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                className={classes.submit}
                isLoading={loading}
                disabled={loading || isLoading}
                sx={{ mt: 2 }}
                buttonText={loading ? "Signing In..." : "Sign In"}
              />

              {showWearablesButton && (
                <>
                  <Box display="flex" alignItems="center" my={3}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" sx={{ mx: 2 }}>
                      OR
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  <SubmitButton
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    className={classes.secondaryLogin}
                    onClick={handleCognitoSignIn}
                    disabled={loading || isLoading}
                    isLoading={isLoading}
                    buttonText={
                      isLoading
                        ? "Signing in to access CDx..."
                        : "Sign in to access CDx"
                    }
                  />
                </>
              )}
            </Form>
          )}
        </Formik>
      </div>

      <Box mt={4} textAlign="center">
        <Copyright />
      </Box>

      <ForgotPasswordModal
        passwordModalOpen={passwordModalOpen}
        closePasswordModal={togglePasswordModal}
      />
    </Container>
  );
}
